import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, FormSkeleton, Status, TextFieldFormik } from '@components';
import { AccountContext, ScreenSizeContext, UserIdentityContext } from '@context';
import { DqpAccountService } from '@data';
import { msalService } from '@services';
import { DqpAccountType, DqpDetailsFormValues, WaterIQUserType } from '@types';
import {
	aussiePhoneRegExp,
	emailRegExp,
	FormEditability,
	FormLabels,
	isDirtyFormOnTrim,
	mapDqpAccountFormValuesToModel,
	mapDqpAccountToFormValues,
} from '@utils';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

// Styles
import styles from './dqpAccountDetails.module.scss';

const dqpDetailsFormLabels: FormLabels<DqpDetailsFormValues> = {
	firstName: 'First name',
	lastName: 'Last name',
	myGovIdEmail: 'Digital ID email address',
	workEmail: 'Work email address',
	workPhone: 'Work phone number',
};

const formValuesEditability: FormEditability<DqpDetailsFormValues> = {
	firstName: false,
	lastName: false,
	myGovIdEmail: false,
	workEmail: true,
	workPhone: true,
};

const dqpDetailsValidationSchema = Yup.object().shape({
	firstName: Yup.string().nullable().optional(),
	lastName: Yup.string().nullable().optional(),
	myGovIdEmail: Yup.string().nullable().optional(),
	workEmail: Yup.string()
		.matches(emailRegExp, `${dqpDetailsFormLabels.workEmail} is not valid`)
		.required(`${dqpDetailsFormLabels.workEmail} is required`),
	workPhone: Yup.string()
		.matches(
			aussiePhoneRegExp,
			`Please enter a valid Australian mobile or landline telephone number`,
		)
		.required(`${dqpDetailsFormLabels.workPhone} is required`),
});

interface DqpAccountDetailsProps {
	onRegisterAsWEHClick?: () => void;
	onCloseAccountAsDQPClick?: () => void;
}

export const DqpAccountDetails = ({
	onRegisterAsWEHClick = () => {
		return;
	},
	onCloseAccountAsDQPClick = () => {
		return;
	},
}: DqpAccountDetailsProps) => {
	// Context
	const { userIdentity } = useContext(UserIdentityContext);
	const { getDqpAccount } = useContext(AccountContext);
	const { isMobile } = useContext(ScreenSizeContext);

	// State
	const [dqpDetailsOriginalValues, setDqpDetailsOriginalValues] = useState<DqpDetailsFormValues>(
		mapDqpAccountToFormValues(userIdentity, getDqpAccount.data),
	);
	const [dqpDetailsInitialValues, setDqpDetailsInitialValues] =
		useState<DqpDetailsFormValues>(dqpDetailsOriginalValues);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [showError, setShowError] = useState(false);

	// Memo
	const hasWEHUserType = useMemo(
		() => msalService.getUserTypes()?.includes(WaterIQUserType.WEH),
		[],
	);

	// Data
	const updateDqpAccount = useMutation({
		mutationFn: (partialDqpAccount: Partial<DqpAccountType>) =>
			DqpAccountService.updateDqpAccount(partialDqpAccount),
		onSuccess: async () => await getDqpAccount.refetch(),
	});

	// Initialization
	useEffect(() => {
		if (userIdentity && getDqpAccount.data) {
			const dqpAccountFormValues = mapDqpAccountToFormValues(userIdentity, getDqpAccount.data);
			setDqpDetailsOriginalValues({ ...dqpAccountFormValues });
			setDqpDetailsInitialValues({ ...dqpAccountFormValues });
		}
	}, [userIdentity, getDqpAccount.data]);

	const isLoadingDqpAccount = () => {
		return getDqpAccount.isLoading;
	};

	const submitForm = async (values: DqpDetailsFormValues) => {
		setDqpDetailsInitialValues(values); // keep inputed values on form while submitting

		const dqpAccountUpdates: Partial<DqpAccountType> = mapDqpAccountFormValuesToModel(
			values,
			formValuesEditability,
		);

		await updateDqpAccount
			.mutateAsync(dqpAccountUpdates)
			.then(() => {
				setShowSuccess(true);
				setIsEditing(false);
			})
			.catch(() => {
				setShowError(true);
			});
	};

	const isEditable = (field: string) => {
		return formValuesEditability[field as keyof DqpDetailsFormValues] ?? false;
	};

	const activateForm = () => {
		setIsEditing(true);
		setShowSuccess(false);
		setShowError(false);
		updateDqpAccount.reset();
	};

	const cancelForm = () => {
		setIsEditing(false);
		setShowError(false);
		setDqpDetailsInitialValues({ ...dqpDetailsOriginalValues });
		updateDqpAccount.reset();
	};

	const StatusComponent = () => {
		return showSuccess || showError ? (
			<div className={styles.status}>
				<Status
					status={showSuccess ? 'success' : 'error'}
					show={true}
					onClose={() => {
						setShowSuccess(false);
						setShowError(false);
					}}
					title={showSuccess ? 'Success' : undefined}
					msg={
						showSuccess
							? 'Updated details have been successfully saved.'
							: 'Submission failed due to server error. Please try again later.'
					}
				/>
			</div>
		) : (
			<></>
		);
	};

	const IdentityDetailsForm = () => (
		<Formik
			initialValues={dqpDetailsInitialValues}
			validationSchema={dqpDetailsValidationSchema}
			onSubmit={submitForm}
		>
			{(
				{ dirty, values, submitForm }: FormikProps<DqpDetailsFormValues>,
				isDisabled = !isEditing || updateDqpAccount.isPending,
			) => (
				<Form>
					{isLoadingDqpAccount() ? (
						<FormSkeleton labels={dqpDetailsFormLabels} />
					) : (
						<div
							className={[styles.grid, updateDqpAccount.isPending && styles['grid--frozen']].join(
								' ',
							)}
						>
							<>
								<label>{dqpDetailsFormLabels.firstName}:</label>
								<TextFieldFormik
									props={{
										name: 'firstName',
										placeholder: 'E.g. Mark',
										disabled: isDisabled || !isEditable('firstName'),
									}}
									size="small"
									testId="firstName"
								/>
							</>
							<>
								<label>{dqpDetailsFormLabels.lastName}:</label>
								<TextFieldFormik
									props={{
										name: 'lastName',
										placeholder: 'E.g. Scott',
										disabled: isDisabled || !isEditable('lastName'),
									}}
									size="small"
									testId="lastName"
								/>
							</>
							<>
								<label>{dqpDetailsFormLabels.myGovIdEmail}:</label>
								<TextFieldFormik
									props={{
										name: 'myGovIdEmail',
										disabled: isDisabled || !isEditable('myGovIdEmail'),
									}}
									testId="myGovIdEmail"
								/>
							</>
							<>
								<label>{dqpDetailsFormLabels.workEmail}:</label>
								<TextFieldFormik
									props={{
										name: 'workEmail',
										disabled: isDisabled || !isEditable('workEmail'),
									}}
									size="medium"
									testId="workEmail"
								/>
							</>
							<>
								<label>{dqpDetailsFormLabels.workPhone}:</label>
								<TextFieldFormik
									props={{
										name: 'workPhone',
										placeholder: '0000000000',
										disabled: isDisabled || !isEditable('workPhone'),
									}}
									size="tiny"
									testId="workPhone"
								/>
							</>
						</div>
					)}
					<StatusComponent />
					<NavigationButtons
						dirty={isDirtyFormOnTrim(dirty, dqpDetailsInitialValues, values)}
						submitForm={submitForm}
					/>
				</Form>
			)}
		</Formik>
	);

	const NavigationButtons = ({
		dirty,
		submitForm,
	}: {
		dirty: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		submitForm: (() => Promise<void>) & (() => Promise<any>);
	}) => (
		<div className={styles['buttons-wrapper']}>
			<div
				className={[
					styles['buttons-container'],
					isEditing && styles['buttons-container--editing'],
				].join(' ')}
			>
				{isEditing && (
					<Button
						key="cancel"
						type="secondary"
						size={isMobile ? 'auto' : 'large'}
						onClick={cancelForm}
						disabled={updateDqpAccount.isPending}
					>
						Cancel
					</Button>
				)}
				<Button
					key={'update'}
					type="primary"
					size={isMobile ? 'auto' : 'large'}
					onClick={() => {
						if (!isEditing) {
							activateForm();
						} else {
							submitForm();
						}
					}}
					isLoading={updateDqpAccount.isPending}
					disabled={
						(!isEditing && isLoadingDqpAccount()) ||
						(isEditing && !dirty && !updateDqpAccount.isPending && !updateDqpAccount.isError)
					}
					data-testid="update"
				>
					{!isEditing ? 'Edit contact details' : 'Save contact details'}
				</Button>
				{!isEditing && (
					<Button
						key={'closeAccount'}
						type="primary"
						size={isMobile ? 'auto' : 'large'}
						onClick={onCloseAccountAsDQPClick}
					>
						Close account
					</Button>
				)}
			</div>
			{!hasWEHUserType && (
				<Button
					key={'registerAsWEH'}
					type="primary"
					size="auto"
					onClick={onRegisterAsWEHClick}
					data-testid="registerAsWEH"
				>
					Register for WaterIQ Water Entitlement Holder
				</Button>
			)}
		</div>
	);

	return <IdentityDetailsForm />;
};
